import React, { useState, useEffect } from "react";
import { Modal, Descriptions, List, Button, message, Skeleton } from "antd";
import dayjs from "dayjs";
import { formatDate } from "../../utils/dateUtils";
import { useAuth } from "../../hooks/useAuth";
import UpdateReviewModal from "./../UpdateReviewModal";
import AttachmentsView from "./../AttachmentsView.js";
import { getSubmittalById } from "../../services/submittalsAPI";
import { useQuery } from "@tanstack/react-query";
import { normalizeBooleanValue } from "../../utils/projectUtils";

const ViewShopDrawingModal = ({ visible, onClose, submittalId }) => {
  const { currentUser, selectedProject } = useAuth();
  const [isUpdateReviewModalVisible, setIsUpdateReviewModalVisible] =
    useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const trackMaterialRelease = normalizeBooleanValue(
    selectedProject?.track_material_release
  );

  const { data: submittal, refetch } = useQuery({
    queryKey: ["submittal", submittalId],
    queryFn: () => getSubmittalById(submittalId),
    enabled: !!submittalId,
    select: (data) => ({
      ...data,
      formatted_number: data.specification_section_details?.section_number
        ? `${data.specification_section_details.section_number}-${data.number}`
        : data.number,
      formatted_ball_in_courts: data?.ball_in_court_details
        .map((element) => element.username)
        .join(", "),
      formatted_received_from: data.received_from_details?.username,
      formatted_spec_section: data.specification_section_details
        ? data.specification_section_details?.title
        : "N/A",
    }),
    onError: (error) => {
      console.log("Failed to fetch submittal", error);
    },
  });

  useEffect(() => {
    if (visible) {
      refetch();
    }
  }, [visible, refetch]);

  const handleUpdateReviewClick = (review) => {
    setSelectedReview(review);
    setIsUpdateReviewModalVisible(true);
  };

  const isAuthorized = (review) => {
    return (
      currentUser?.id === submittal.submittal_manager_id ||
      (review.reviewer_id === currentUser?.id && submittal.ball_in_court_ids
        ? submittal.ball_in_court_ids.some(
            (element) => element === currentUser?.id
          )
        : false) ||
      selectedProject?.role === "project_manager" ||
      selectedProject?.role === "owner"
    );
  };

  return (
    <Modal
      title="View Shop Drawing"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
    >
      {" "}
      {!submittal ? (
        <Skeleton active />
      ) : (
        <>
          <Descriptions bordered column={1} labelStyle={{ width: "15rem" }}>
            <Descriptions.Item label="Title">
              {submittal.title}
            </Descriptions.Item>
            <Descriptions.Item label="Description">
              {submittal.description}
            </Descriptions.Item>
            <Descriptions.Item label="Number">
              {submittal.number}
            </Descriptions.Item>
            <Descriptions.Item label="Revision">
              {submittal.revision}
            </Descriptions.Item>
            <Descriptions.Item label="Specification Section">
              {submittal.specification_section_details
                ? `${submittal.specification_section_details.section_number} ${submittal.specification_section_details.title}`
                : "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Issue Date">
              {submittal.issue_date
                ? formatDate(submittal.issue_date)
                : "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Due Date">
              {submittal.due_date
                ? formatDate(submittal.due_date)
                : "N/A"}
            </Descriptions.Item>
            {trackMaterialRelease && (
              <Descriptions.Item label="Released On">
                {submittal.released_on
                  ? formatDate(submittal.released_on)
                  : "N/A"}
              </Descriptions.Item>
            )}
            {trackMaterialRelease && (
              <Descriptions.Item label="Estimated Delivery Date">
                {submittal.estimated_delivery_date
                  ? formatDate(submittal.estimated_delivery_date)
                  : "N/A"}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Status">
              {submittal.status}
            </Descriptions.Item>
            <Descriptions.Item label="Priority">
              {submittal.priority || "Medium"}
            </Descriptions.Item>
            <Descriptions.Item label="EDMS">
              {submittal.edms || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Submitted By">
              {submittal.submitted_by_details.username}
            </Descriptions.Item>
            <Descriptions.Item label="Received From">
              {submittal.received_from_details?.username || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Responsible Contractor">
              {submittal.responsible_contractor_details?.name || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Shop Drawing Manager">
              {submittal.submittal_manager_details.username}
            </Descriptions.Item>
            <Descriptions.Item label="Ball in Court">
              {submittal.ball_in_court_details
                .map((person) => person.username)
                .join(", ")}
            </Descriptions.Item>
            <Descriptions.Item label="Cc">
              {submittal.distribution_list_details
                .map((person) => person.username)
                .join(", ")}
            </Descriptions.Item>
            <Descriptions.Item label="Attachments">
              <AttachmentsView attachments={submittal.attachments} />
            </Descriptions.Item>
            <Descriptions.Item label="Reviewers">
              <List
                dataSource={submittal.steps}
                renderItem={(step) => (
                  <List.Item>
                    <List.Item.Meta
                      title={`Step ${step.step_number}`}
                      description={
                        <List
                          dataSource={step.reviews}
                          renderItem={(review) => (
                            <List.Item
                              actions={[
                                isAuthorized(review) && (
                                  <Button
                                    type="link"
                                    onClick={() =>
                                      handleUpdateReviewClick({
                                        ...review,
                                        submittal_title: submittal.title,
                                        submittal_number: submittal.number,
                                        submittal_ball_in_court:
                                          submittal.ball_in_court_details,
                                      })
                                    }
                                  >
                                    Update Review
                                  </Button>
                                ),
                              ]}
                            >
                              <List.Item.Meta
                                title={review.reviewer_details.username}
                                description={
                                  <>
                                    <div>Status: {review.status || "N/A"}</div>
                                    <div>
                                      Comment: {review.comment || "N/A"}
                                    </div>
                                    <div>
                                      Reviewed On:{" "}
                                      {review.reviewed_on
                                        ? dayjs(review.reviewed_on).format(
                                            "YYYY-MM-DD"
                                          )
                                        : "N/A"}
                                    </div>
                                    <div>
                                      <AttachmentsView
                                        attachments={review.attachments}
                                      />
                                    </div>
                                  </>
                                }
                              />
                            </List.Item>
                          )}
                        />
                      }
                    />
                  </List.Item>
                )}
              />
            </Descriptions.Item>
          </Descriptions>
          {selectedReview && (
            <UpdateReviewModal
              visible={isUpdateReviewModalVisible}
              onClose={() => {
                setIsUpdateReviewModalVisible(false);
              }}
              review={selectedReview}
              onCloseSubmittalModal={onClose}
            />
          )}
        </>
      )}
    </Modal>
  );
};

export default ViewShopDrawingModal;
